import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/common/Icon';
import ModalWrapper from '../components/common/ModalWrapper';
import { useAuth } from '../contexts';
import { useMoney } from './useMoney';
import { SUPPORTED_PREFIXES } from '../i18n/bootstrap';

// 신규가입 환영 쿠폰 금액(KRW) — VIPWELCOME 쿠폰값. 다국어 UI 에선 useMoney 가 USD 환산.
const WELCOME_COUPON_AMOUNT_KRW = 14900;

// #245: 쿠폰코드 클립보드 복사 — navigator.clipboard 가 인앱 웹뷰/비보안 컨텍스트에서 실패하므로
//   execCommand('copy') textarea fallback 을 함께 둬 어디서든 복사되게 한다.
function fallbackCopy(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
function copyCouponCode(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
      return;
    }
  } catch { /* noop */ }
  fallbackCopy(text);
}

/**
 * 배너 link_url 클릭 처리 공통 훅 (기존 ImageSlider 로직 추출 — 슬라이더/고정배너 공용).
 * link_url 형식:
 *   - "coupon:CODE"         → 쿠폰 코드 복사 + 모달
 *   - "signup-coupon:CODE"  → 비로그인은 /signup, 로그인은 코드 복사 + 환영 모달
 *   - "http(s)://..."       → 외부 링크 새 창
 *   - "/en/..." 등 언어 prefix 내부 경로 → 해당 언어 페이지로 전체 로드(어드민 의도 존중, #243)
 *   - prefix 없는 내부 경로 → 현재 언어 그대로 SPA 이동
 *
 * @returns {{ onBannerLink: (linkUrl: string) => void, couponModal: React.ReactNode }}
 */
export default function useBannerLink() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const money = useMoney();
  const { user } = useAuth();
  // copiedCoupon: { code, isWelcome } | null
  const [copiedCoupon, setCopiedCoupon] = useState(null);

  const onBannerLink = useCallback((linkUrl) => {
    if (!linkUrl) return;

    const signupCouponMatch = linkUrl.match(/^signup-coupon:(.+)$/);
    if (signupCouponMatch) {
      if (!user) { navigate('/signup'); return; }
      const code = signupCouponMatch[1].trim();
      copyCouponCode(code);
      setCopiedCoupon({ code, isWelcome: true });
      return;
    }

    const couponMatch = linkUrl.match(/^coupon:(.+)$/);
    if (couponMatch) {
      const code = couponMatch[1].trim();
      copyCouponCode(code);
      setCopiedCoupon({ code, isWelcome: false });
      return;
    }

    // 외부 전체 URL → 새 창
    if (/^https?:\/\//i.test(linkUrl)) {
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // 내부 경로 이동.
    const path = linkUrl.startsWith('/') ? linkUrl : `/${linkUrl}`;
    const seg = path.split('/')[1];
    // #243: 언어 prefix(/en·/ja 등) 가 명시되면 그 언어 페이지로 전체 로드(basename 재설정 필요).
    if (SUPPORTED_PREFIXES.includes(seg)) {
      window.location.assign(path);
      return;
    }
    navigate(path || '/');
  }, [navigate, user]);

  const couponModal = copiedCoupon ? (
    <ModalWrapper
      onClose={() => setCopiedCoupon(null)}
      maxWidth="modal"
      variant="centered"
      padding="px-5 sm:px-8 pt-8 sm:pt-10 pb-5 sm:pb-6"
      gap="gap-4 sm:gap-5"
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-4">
          <Icon name="check" size={28} className="text-orange-500" />
        </div>
        <h2 className="text-lg font-bold text-mono-900 mb-2">
          {copiedCoupon.isWelcome ? t('payment.welcome_coupon_copied_title') : t('payment.coupon_copied_title')}
        </h2>
        <p className="text-[15px] text-mono-600 mb-4 whitespace-pre-line">
          {copiedCoupon.isWelcome
            ? t('payment.welcome_coupon_copied_desc', { amount: money(WELCOME_COUPON_AMOUNT_KRW) })
            : t('payment.coupon_copied_desc')}
        </p>
        {!copiedCoupon.isWelcome && (
          <div className="inline-block px-4 py-2 rounded-lg bg-orange-50 border border-orange-200 font-mono text-base font-semibold text-orange-600">
            {copiedCoupon.code}
          </div>
        )}
      </div>
      <button
        onClick={() => setCopiedCoupon(null)}
        className="w-full py-3 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 transition"
      >
        {t('common.confirm')}
      </button>
    </ModalWrapper>
  ) : null;

  return { onBannerLink, couponModal };
}
